// SortMenu.tsx — comment sort dropdown (Most relevant / Newest / Oldest) using ui/Popover.
import { useState } from 'react';
import styled from 'styled-components';
import { Popover } from '@/ui';
import { Icon } from '@/icons';
import type { SortId } from './useCommentThread';

const OPTIONS: Array<{ id: SortId; label: string }> = [
  { id: 'most-relevant', label: 'Most relevant' },
  { id: 'most-liked', label: 'Most cherished' },
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
];

export interface SortMenuProps {
  sort: SortId;
  onChange: (s: SortId) => void;
}

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 6px 10px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const MenuItem = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  & .check {
    visibility: ${({ $selected }) => ($selected ? 'visible' : 'hidden')};
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

export function SortMenu({ sort, onChange }: SortMenuProps) {
  const [open, setOpen] = useState(false);
  const current = OPTIONS.find((o) => o.id === sort) ?? OPTIONS[0]!;
  return (
    <Popover
      trigger={
        <Trigger type="button">
          {current.label}
          <Icon name="chevron-down" size={14} />
        </Trigger>
      }
      open={open}
      onOpenChange={setOpen}
    >
      {OPTIONS.map((o) => (
        <MenuItem
          key={o.id}
          type="button"
          role="menuitemradio"
          aria-checked={o.id === sort}
          $selected={o.id === sort}
          onClick={() => {
            onChange(o.id);
            setOpen(false);
          }}
        >
          <Icon name="check" size={14} className="check" />
          {o.label}
        </MenuItem>
      ))}
    </Popover>
  );
}
